'use client';

import { useEffect, useState } from 'react';

import { AdTrackingPlugin } from '@snowplow/browser-plugin-ad-tracking';
import { ConsentPlugin } from '@snowplow/browser-plugin-consent';
import { FormTrackingPlugin, enableFormTracking } from '@snowplow/browser-plugin-form-tracking';
import { LinkClickTrackingPlugin } from '@snowplow/browser-plugin-link-click-tracking';
import { PerformanceTimingPlugin } from '@snowplow/browser-plugin-performance-timing';
import { WebVitalsPlugin } from '@snowplow/browser-plugin-web-vitals';

import { newTracker, enableActivityTracking, BrowserTracker } from '@snowplow/browser-tracker';

import { useSetAtom } from 'jotai';
import { usePathname } from 'next/navigation';
import { env } from 'next-runtime-env';

import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import { Tenant } from '@core/Entities/App/Tenants.entity';
import { clientLog } from '@core/Utils/Log/ClientLog';

import { generateSnowplowAppId } from '../Utils/GenerateSnowplowAppId';

export const SnowplowScript = ({ tenant }: { tenant?: Tenant }) => {
  const pathname = usePathname();
  const setUserId = useSetAtom(userIdAtom);
  const [tracker, setTracker] = useState<BrowserTracker | null>(null);

  useEffect(() => {
    const collectorUrl = `${window.location.origin}`.includes('http://localhost:300')
      ? 'localhost:9090'
      : env('NEXT_PUBLIC_SNOWPLOW_COLLECTOR_URL')!;

    if (!collectorUrl) {
      console.error('No collector URL for Snowplow found');
      return;
    }

    const tracker = newTracker('sp_i15', collectorUrl, {
      appId: generateSnowplowAppId({ app: tenant }),
      plugins: [
        LinkClickTrackingPlugin(),
        FormTrackingPlugin(),
        AdTrackingPlugin(),
        WebVitalsPlugin(),
        PerformanceTimingPlugin(),
        ConsentPlugin(),
      ],
      stateStorageStrategy: 'cookie',
    });

    setTracker(tracker);
  }, []);

  useEffect(() => {
    enableActivityTracking({ minimumVisitLength: 30, heartbeatDelay: 10 });
    enableFormTracking();
  }, [pathname]);

  useEffect(() => {
    if (!tracker) return;

    const snowplowUserId = tracker?.getDomainUserId() as unknown as string | null;

    // if we already have a userId then it's likely been resolved
    setUserId(userId => {
      const clientUserId = userId || snowplowUserId;

      tracker.setUserId(clientUserId);

      clientLog({ data: clientUserId, label: 'Client user ID' });
      return clientUserId;
    });

    clientLog({ data: snowplowUserId, label: 'Snowplow user ID' });
  }, [tracker]);

  return null;
};
